// フロントエンド（React）側の Sentry 初期化ヘルパ。
//
// VITE_SENTRY_DSN はビルド時に埋め込まれる（Dockerfile.prod の build args → import.meta.env）。
// DSN が空なら initSentry() は何もしない = 開発ビルドやローカルでは無効。
//
// - Session Replay は入れない（本アプリは自前 rrweb 録画を持つため二重計測を避ける）。
// - tracesSampleRate は低め（コスト抑制）。VITE_SENTRY_TRACES_SAMPLE_RATE で上書き可。
// - ユーザーコンテキスト（id + email）は Inertia の shared props から setSentryUser で紐付ける。
import * as Sentry from "@sentry/react";

let initialized = false;

/**
 * Sentry を初期化する。VITE_SENTRY_DSN が空、または既に初期化済みなら何もしない。
 * エントリポイント（application.tsx）の最初期に 1 回だけ呼ぶ。
 */
export function initSentry(): boolean {
  if (initialized) return true;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return false;

  const rawRate = import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as
    | string
    | undefined;
  const tracesSampleRate = rawRate ? Number(rawRate) : 0.1;

  Sentry.init({
    dsn,
    environment:
      (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ||
      (import.meta.env.MODE as string),
    // 明示的に紐付けた user のみ送る。デフォルト PII（IP 等）は送らない。
    sendDefaultPii: false,
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.1,
  });

  initialized = true;
  return true;
}

/** Sentry 初期化済みかどうか（テスト・条件分岐用）。 */
export function isSentryEnabled(): boolean {
  return initialized;
}

export type SentryUser = {
  id?: number | string | null;
  email?: string | null;
};

/**
 * ログイン中ユーザーを Sentry に紐付ける。未初期化なら no-op。
 * id / email がどちらも無ければ user context をクリアする。
 *
 * @param user Inertia shared props 由来の { id, email }
 */
export function setSentryUser(user: SentryUser | null | undefined): void {
  if (!initialized) return;

  if (!user || (user.id == null && !user.email)) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id == null ? undefined : String(user.id),
    email: user.email ?? undefined,
  });
}
